import { useCallback, useState } from 'react';
import { Loader2, Paperclip, X } from 'lucide-react';
import type { RequestAttachment } from '../../services/supportRequest.service';

export type PendingUpload = {
  key: string;
  file: File;
  progress: number;
  attachmentId?: string;
  error?: string;
};

type UploadFn = (args: {
  file: File;
  onProgress?: (pct: number) => void;
}) => Promise<RequestAttachment>;

/**
 * Files staged for a message. Each one starts uploading the moment it's added,
 * so by the time the person sends, the bytes are usually already in S3 and
 * only the ids travel with the message.
 *
 * Shared by the reply composer and the "new request" dialog — they differ only
 * in where the bytes are uploaded to.
 */
export function usePendingUploads(upload: UploadFn) {
  const [pending, setPending] = useState<PendingUpload[]>([]);

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files);
      list.forEach((file) => {
        const key = `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`;
        setPending((prev) => [...prev, { key, file, progress: 0 }]);

        upload({
          file,
          onProgress: (pct) =>
            setPending((prev) =>
              prev.map((p) => (p.key === key ? { ...p, progress: pct } : p)),
            ),
        })
          .then((att) =>
            setPending((prev) =>
              prev.map((p) =>
                p.key === key ? { ...p, attachmentId: att.id, progress: 100 } : p,
              ),
            ),
          )
          .catch((e: Error) =>
            setPending((prev) =>
              prev.map((p) => (p.key === key ? { ...p, error: e.message } : p)),
            ),
          );
      });
    },
    [upload],
  );

  const remove = useCallback(
    (key: string) => setPending((prev) => prev.filter((x) => x.key !== key)),
    [],
  );
  const clear = useCallback(() => setPending([]), []);

  const uploading = pending.some((p) => !p.attachmentId && !p.error);
  // A failed file would otherwise just be left off the message. Callers block
  // sending until it's removed, so nothing goes out missing a file the person
  // thinks they attached.
  const failed = pending.some((p) => !!p.error);
  const attachmentIds = pending
    .map((p) => p.attachmentId)
    .filter((x): x is string => !!x);

  return { pending, addFiles, remove, clear, uploading, failed, attachmentIds };
}

export function PendingUploadChips({
  pending,
  onRemove,
}: {
  pending: PendingUpload[];
  onRemove: (key: string) => void;
}) {
  if (pending.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-2">
      {pending.map((p) => (
        <div
          key={p.key}
          title={p.file.name}
          className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 text-xs max-w-[min(20rem,100%)] ${
            p.error
              ? 'border-destructive/50 text-destructive'
              : 'border-border bg-muted/40'
          }`}
        >
          {!p.attachmentId && !p.error ? (
            <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
          ) : (
            <Paperclip className="h-3 w-3 flex-shrink-0" />
          )}
          <span className="truncate min-w-0">{p.file.name}</span>
          {!p.attachmentId && !p.error && (
            <span className="text-muted-foreground">{p.progress}%</span>
          )}
          {p.error && <span className="truncate min-w-0">— {p.error}</span>}
          <button
            type="button"
            onClick={() => onRemove(p.key)}
            className="text-muted-foreground hover:text-foreground flex-shrink-0"
            aria-label={`Remove ${p.file.name}`}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  );
}
